import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { errorProps, successProps } from "../utils";
import { toast } from "react-toastify";
import toLower from "lodash/toLower";
import usePublicDivision from "./usePublicDivision";
import useRegionLocation from "./useRegionLocation";
import {
  useCreateJobMutation,
  useUpdateJobMutation,
} from "../store/query/jobs";
import { useNavigate } from "react-router-dom";

const usePostJob = ({ job, id, user }) => {
  const [canSubmit, setCanSubmit] = useState(false);
  const [createJob, { isLoading: isCreatingJob }] = useCreateJobMutation();
  const [updateJob, { isLoading: isUpdatingJob }] = useUpdateJobMutation();
  const { divisonList } = usePublicDivision();
  const findSingleDivision = useCallback(() => {
    return divisonList?.find(
      (i) => toLower(i.label) === toLower(job?.division || user?.division?.name)
    );
  }, [divisonList, job?.division, user?.division]);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      id: "",
      company_name: "",
      contact_email: "",
      phone: "",
      nationality: "Nigeria",
      title: "",
      description: "",
      state: null,
      city: null,
      address: "",
      divisionObj: null,
      hourly_rate: "",
      responsibilities: [],
      requirements: [],
      timesheet: [],
    },
    validateOnMount: true,
    validate: (values) => {
      const errors = {};

      if (!values.company_name)
        errors.company_name = "Company name is required";
      if (!values.contact_email) errors.contact_email = "Email is required";
      if (!values.phone) {
        errors.phone = "Phone number is required";
      } else if (!/^(?:\+?234|0)[789][01]\d{8}$/.test(values.phone)) {
        errors.phone = "Enter a valid Nigerian phone number";
      }
      if (!values.title) {
        errors.title = "Title is required";
      } else if (values.title.trim().length < 5) {
        errors.title = "Title must be minimum of 5 and less than 25 characters";
      } else if (values.title.trim().length > 25) {
        errors.title = "Title must be minimum of 5 and less than 25 characters";
      }
      if (!values.description) {
        errors.description = "Description is required";
      } else if (values.description.trim().split(/\s+/).length > 250) {
        errors.description = "Description must be 20 words or less";
      }
      if (!values.hourly_rate) errors.hourly_rate = "Hourly rate is required";
      if (!values.responsibilities || values.responsibilities.length === 0)
        errors.responsibilities = "At least one responsibility is required";
      if (!values.requirements || values.requirements.length === 0)
        errors.requirements = "At least one requirement is required";
      if (!values.timesheet || values.timesheet.length === 0)
        errors.timesheet = "At least one timesheet entry is required";
      if (!values.divisionObj) errors.divisionObj = "Division is required";
      if (!values.state) errors.state = "State is required";
      if (!values.city) errors.city = "City is required";
      if (!values.address) errors.address = "Address is required";

      setCanSubmit(Object.keys(errors).length === 0);

      return errors;
    },
    onSubmit: (values) => {
      const { divisionObj, state, city, id, ...data } = values;
      const body = {
        division_id: divisionObj.value,
        state_id: state.value,
        city_id: city.value,
        type: user?.account_type,
        ...data,
      };
      if (id.length === 0) {
        handleSubmit(body);
      } else {
        const { responsibilities, requirements, timesheet, ...data } = body;
        handleUpdate(id, data);
      }
    },
  });

  const { states, cities, setStateId } = useRegionLocation();

  const { setValues, values } = formik;
  //console.log("values", values);

  const selectedProvinceOption =
    useMemo(() => {
      return states?.find((i) => toLower(i.label) === toLower(job?.state));
    }, [states, job?.state]) || values?.state;

  const cityOption = useMemo(() => {
    return cities?.find(
      (i) => toLower(i.label) === toLower(job?.city || values.city?.name)
    );
  }, [cities, job?.city, values.city]);

  const state = selectedProvinceOption;
  const city = cityOption;

  useEffect(() => {
    setStateId(state?.value);
  }, [state?.value, setStateId]);

  useEffect(() => {
    if (id) {
      setValues({
        id: job?.id,
        company_name: job?.company_name,
        contact_email: job?.contact_email,
        phone: job?.phone,
        nationality: "Nigeria",
        title: job?.title,
        description: job?.description,
        divisionObj: findSingleDivision(),
        state: selectedProvinceOption,
        city: cityOption,
        address: job?.address,
        hourly_rate: job?.hourly_rate,
        responsibilities: job?.responsibilities || [],
        requirements: job?.requirements || [],
        timesheet: job?.timesheet || [],
      });
    }
  }, [
    id,
    job,
    selectedProvinceOption,
    cityOption,
    findSingleDivision,
    setValues,
  ]);

  const handleUpdate = async (id, data) => {
    const values = {
      ...data,
      id,
      _method: "PUT",
    };

    const res = await updateJob(values);
    //console.log("update", res, values);
    // if (res?.data?.status) {
    //   toast.success("Job Updated Successfully", successProps);
    //   navigate(`/joblistings/${id}/posted`);
    // } else {
    //   toast.error("Failed to update", errorProps);
    // }

    if ("data" in res) {
      const { data } = res;
      if (data.status === true) {
        toast.success("Job Updated Successfully", successProps);
        navigate(`/joblistings/${id}/posted`);
        //console.log("success", res);
      } else {
        //console.log(data);
        toast.error(data?.errors[0] ?? "Failed to Create Job", errorProps);
      }
    } else {
      //console.log(res);
      const firstError = Object.values(res?.error?.data?.errors ?? {})[0]?.[0];

      toast.error(firstError ?? "Failed to Updating Job", errorProps);
    }
  };

  const handleSubmit = async (values) => {
    const res = await createJob(values);
    //console.log("submit", values, res);

    if ("data" in res) {
      const { data } = res;
      if (data.status === true) {
        toast.success(data?.message, successProps);
        navigate(`/joblistings/${data?.job_id}/posted`);

        formik.handleReset();
      } else {
        toast.error(data?.errors[0] ?? "Failed to Create Job", errorProps);
      }
    } else {
      const firstError = Object.values(res?.error?.data?.errors ?? {})[0]?.[0];

      toast.error(firstError ?? "Failed to Create Job", errorProps);
    }
  };

  return {
    ...formik,
    canSubmit,
    loading: isCreatingJob || isUpdatingJob,
    states,
    cities,
    state,
    city,
  };
};

export default usePostJob;
